import React, { useEffect, useState } from 'react';
import client from '../../../api/client';
import { diagnoseIfNotZipBlob } from '../../../api/reportBinaryGuard';
import ReportParamsForm, { validateReportParams } from './ReportParamsForm';

/**
 * Modal that launches one report on demand. Built on top of the report's
 * declared <c>parameters</c> schema so the same component handles every
 * report we register — it does not need to know which report it's running.
 *
 * On confirm: POST /api/reporting/run with `{ reportId, scope, dateRange,
 * repSelection, removeTestData }`, save the response blob as a download,
 * close the dialog. Fields the schema doesn't ask for (e.g. scope when
 * `omitScope`) are still sent so the server can re-validate on its own
 * — the controller short-circuits resolution per the schema flags.
 */
function RunNowDialog({ report, customers, savedFilters, csReps, onClose }) {
  const [params, setParams] = useState(() => buildInitialParams(report));
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setParams(buildInitialParams(report));
    setError('');
  }, [report]);

  if (!report) return null;

  const validationError = validateReportParams(report.parameters, params);
  const isValid = validationError === null;

  const handleRun = async () => {
    setRunning(true);
    setError('');
    try {
      const body = {
        reportId: report.id,
        scope: params.scope,
        dateRange: params.dateRange,
        repSelection: params.repSelection,
        removeTestData: params.removeTestData,
        removeSelfReferrals: params.removeSelfReferrals,
        consultEventNames: params.consultEventNames,
        procedureEventNames: params.procedureEventNames,
      };
      const res = await client.post('/api/reporting/run', body, { responseType: 'blob' });

      const blob = res.data instanceof Blob ? res.data : new Blob([res.data]);
      const fmt = String(report.outputFormat || '').toLowerCase();
      if (fmt === 'pptx' || fmt === 'xlsx' || fmt === 'zip') {
        const bad = await diagnoseIfNotZipBlob(blob);
        if (bad) {
          throw new Error(
            `${bad} If DevTools Network shows 200 OK but this persists, inspect the Response tab (should start with ZIP "PK").`
          );
        }
      }

      const cd = res.headers['content-disposition'] || '';
      const m = /filename="?([^";]+)"?/i.exec(cd);
      const fileName = m ? m[1] : `${report.id}.${report.outputFormat}`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      onClose?.();
    } catch (err) {
      let msg = err.message;
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          msg = json.error || msg;
        } catch { /* leave msg */ }
      } else if (err.response?.data?.error) {
        msg = err.response.data.error;
      }
      setError(msg || 'Report run failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="agentic-modal-backdrop" onClick={onClose}>
      <div className="agentic-modal" onClick={(e) => e.stopPropagation()}>
        <div className="agentic-modal-header">
          <h3>Run report — {report.displayName}</h3>
          <button className="agentic-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="agentic-modal-body">
          <p className="agentic-modal-desc">{report.description}</p>
          <div className="agentic-modal-meta">
            <span className="agentic-format-pill">{(report.outputFormat || '').toUpperCase()}</span>
            {report.parameters?.containsPhi && (
              <span className="agentic-phi-pill">Contains PHI</span>
            )}
            {report.parameters?.excludesFromScheduledEvents && (
              <span className="agentic-runonly-pill">Not schedulable</span>
            )}
          </div>

          <ReportParamsForm
            schema={report.parameters}
            value={params}
            onChange={setParams}
            customers={customers}
            savedFilters={savedFilters}
            csReps={csReps}
          />

          {error && <div className="agentic-error">{error}</div>}
        </div>

        <div className="agentic-modal-footer">
          <button type="button" className="agentic-btn-secondary" onClick={onClose} disabled={running}>
            Cancel
          </button>
          <button
            type="button"
            className="agentic-btn-primary"
            onClick={handleRun}
            disabled={running || !isValid}
            title={isValid ? '' : validationError || ''}
          >
            {running ? 'Generating…' : 'Generate & Download'}
          </button>
        </div>
      </div>
    </div>
  );
}

function buildInitialParams(report) {
  const schema = report?.parameters || {};
  return {
    scope: { type: schema.defaultScopeKind || 'all' },
    dateRange: schema.defaultDateRange || { mode: 'last_n_days', n: 30 },
    repSelection: schema.supportsRepSelection ? { mode: 'all' } : undefined,
    removeTestData: true,
    removeSelfReferrals: schema.supportsRemoveSelfReferrals ? false : undefined,
    consultEventNames: schema.supportsScheduleEventAliases ? [] : undefined,
    procedureEventNames: schema.supportsScheduleEventAliases ? [] : undefined,
  };
}

export default RunNowDialog;
